/**
 * App Layout - Main application layout with responsive sidebar
 * Phase 2: Responsive layout with collapsible sidebar for mobile
 */

import { useState, useEffect, useMemo } from 'react';
import { Layout, Menu, Typography, Avatar, Dropdown, Button, message, Badge } from 'antd';
import type { MenuProps } from 'antd';
import { signOut } from 'firebase/auth';
import { auth } from '@/config/firebase';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useEffectiveUserId } from '@/hooks/useEffectiveUserId';
import { MaterialIcon } from './MaterialIcon';
import { StorageBar } from './StorageBar';
import { WhatsAppSettings } from './WhatsAppSettings';
import { subscribeToAdminStatus } from '@/services/userService';
import { subscribeToVideoJobs, getActiveJobsCount } from '@/services/videoService';
import type { VideoJob } from '@/types';
import { ImpersonationBanner } from './ImpersonationBanner';
import { ImpersonateUserModal } from './ImpersonateUserModal';
import styles from './AppLayout.module.css';
import { BRANDING } from '@/constants/branding';

const { Header, Content, Sider } = Layout;
const { Text } = Typography;

interface AppLayoutProps {
  children: React.ReactNode;
}

export default function AppLayout({ children }: AppLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const { effectiveUid } = useEffectiveUserId();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [videoJobs, setVideoJobs] = useState<VideoJob[]>([]);
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [impersonateModalOpen, setImpersonateModalOpen] = useState(false);

  const activeJobsCount = useMemo(() => getActiveJobsCount(videoJobs), [videoJobs]);

  // Subscribe to admin status when user changes
  useEffect(() => {
    if (!user) {
      setIsAdmin(false);
      return;
    }

    const unsubscribe = subscribeToAdminStatus((adminStatus) => {
      setIsAdmin(adminStatus);
    }, user.uid);

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user]);

  // Subscribe to video jobs for progress notifications
  useEffect(() => {
    if (!effectiveUid) return;

    const unsubscribe = subscribeToVideoJobs(effectiveUid, setVideoJobs);
    return () => unsubscribe();
  }, [effectiveUid]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/login');
  };

  const handleCopyUserId = () => {
    if (user?.uid) {
      navigator.clipboard.writeText(user.uid);
      message.success('User ID copied to clipboard');
    }
  };

  // Determine active menu key based on current route
  const getSelectedKey = () => {
    if (location.pathname === '/') return 'projects';
    if (location.pathname === '/account') return 'account';
    if (location.pathname === '/usage') return 'usage';
    if (location.pathname === '/videos') return 'videos';
    if (location.pathname === '/map') return 'map';
    if (location.pathname === '/sync') return 'sync';
    if (location.pathname === '/mobile-app') return 'mobile-app';
    if (location.pathname.startsWith('/admin')) return 'admin';
    return 'projects';
  };

  // Sidebar menu items
  const menuItems: MenuProps['items'] = [
    {
      key: 'projects',
      icon: <MaterialIcon name="photo_camera" size={21} />,
      label: 'Views',
      onClick: () => {
        navigate('/');
        setMobileMenuVisible(false);
      },
    },
    {
      key: 'videos',
      icon: <MaterialIcon name="movie" size={21} />,
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Videos
          {activeJobsCount > 0 && (
            <Badge count={activeJobsCount} size="small" />
          )}
        </span>
      ),
      onClick: () => {
        navigate('/videos');
        setMobileMenuVisible(false);
      },
    },
    {
      key: 'whatsapp-alerts',
      icon: <MaterialIcon name="notifications" size={21} />,
      label: 'Alerts',
      onClick: () => {
        setMobileMenuVisible(false);
        setWhatsappOpen(true);
      },
    },
    {
      key: 'map',
      icon: <MaterialIcon name="location_on" size={21} />,
      label: 'Locations',
      onClick: () => {
        navigate('/map');
        setMobileMenuVisible(false);
      },
    },
    {
      key: 'usage',
      icon: <MaterialIcon name="bar_chart" size={21} />,
      label: 'Usage',
      onClick: () => {
        navigate('/usage');
        setMobileMenuVisible(false);
      },
    },
    {
      key: 'sync',
      icon: <MaterialIcon name="cloud_sync" size={21} />,
      label: 'Cloud Sync',
      onClick: () => {
        navigate('/sync');
        setMobileMenuVisible(false);
      },
    },
    // Admin menu item - only visible to admins
    ...(isAdmin ? [{
      key: 'admin',
      icon: <MaterialIcon name="admin_panel_settings" size={21} />,
      label: 'Admin',
      onClick: () => {
        navigate('/admin');
        setMobileMenuVisible(false);
      },
    }] : []),
  ];

  // User dropdown menu — every action also closes the mobile sidebar so a
  // newly opened modal/page is not stacked underneath it.
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'email',
      label: user?.email,
      disabled: true,
    },
    {
      type: 'divider',
    },
    {
      key: 'account',
      icon: <MaterialIcon name="credit_card" />,
      label: 'Plans',
      onClick: () => {
        setMobileMenuVisible(false);
        navigate('/account');
      },
    },
    {
      key: 'copy-user-id',
      icon: <MaterialIcon name="content_copy" />,
      label: 'Copy User ID',
      onClick: () => {
        setMobileMenuVisible(false);
        handleCopyUserId();
      },
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <MaterialIcon name="logout" />,
      label: 'Logout',
      onClick: () => {
        setMobileMenuVisible(false);
        handleLogout();
      },
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <ImpersonationBanner />
    <Layout style={{ flex: 1 }}>
      {/* Mobile Menu Overlay */}
      {collapsed && mobileMenuVisible && (
        <div
          onClick={() => setMobileMenuVisible(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
            zIndex: 998,
          }}
        />
      )}

      {/* Responsive Sidebar */}
      <Sider
        width={220}
        breakpoint="lg"
        collapsedWidth="0"
        collapsed={collapsed && !mobileMenuVisible}
        onBreakpoint={(broken) => {
          setCollapsed(broken);
          if (broken) {
            setMobileMenuVisible(false);
          }
        }}
        onCollapse={(isCollapsed) => {
          if (!collapsed) {
            // Only update state if not in mobile mode
            setCollapsed(isCollapsed);
          }
        }}
        style={{
          overflow: 'hidden',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: collapsed ? 999 : 1,
        }}
      >
        {/* Sidebar content wrapper for flex layout */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {/* Logo/Brand — paddingLeft aligns icon with nav menu icons */}
          <div
            style={{
              height: 64,
              display: 'flex',
              alignItems: 'center',
              paddingLeft: 24,
              gap: 8,
              color: 'white',
              fontSize: 18,
              fontWeight: 'bold',
              flexShrink: 0,
            }}
          >
            <img src="/img/masterlapse-logo.png" alt="" style={{ width: 28, height: 28 }} />
            {BRANDING.APP_NAME}
          </div>

          {/* Navigation Menu */}
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[getSelectedKey()]}
            items={menuItems}
            style={{ flex: 1, overflow: 'auto' }}
          />

          {/* Storage Bar - at the bottom */}
          <div style={{ flexShrink: 0 }}>
            <StorageBar />
          </div>
        </div>
      </Sider>

      {/* Main Layout */}
      <Layout style={{ marginLeft: collapsed ? 0 : 220 }}>
        {/* Header - reduced height for more content space */}
        <Header className="px-6 flex items-center justify-between border-b border-gray-200 bg-white" style={{ height: 48, lineHeight: '48px' }}>

          {/* Left side: Burger Menu Button (visible only on mobile when collapsed) */}
          <div>
            {collapsed && (
              <Button
                type="text"
                icon={<MaterialIcon name={mobileMenuVisible ? 'menu_open' : 'menu'} size={22} />}
                onClick={() => setMobileMenuVisible(!mobileMenuVisible)}
                style={{
                  fontSize: '16px',
                  width: 48,
                  height: 48,
                }}
              />
            )}
          </div>

          {/* Right side: Impersonation + User Profile */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isAdmin && (
              <Button
                type="text"
                icon={<MaterialIcon name="supervisor_account" size={22} />}
                onClick={() => setImpersonateModalOpen(true)}
                title="Impersonate user"
                style={{ width: 36, height: 36 }}
              />
            )}
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar icon={<MaterialIcon name="person" />} />
                <Text>{user?.email?.split('@')[0]}</Text>
              </div>
            </Dropdown>
          </div>
        </Header>

        {/* Content Area */}
        <Content className={styles.content}>
          {children}
        </Content>
      </Layout>

      {/* WhatsApp Settings Modal */}
      <WhatsAppSettings open={whatsappOpen} onClose={() => setWhatsappOpen(false)} />

      {/* Impersonate User Modal */}
      <ImpersonateUserModal
        open={impersonateModalOpen}
        onClose={() => setImpersonateModalOpen(false)}
      />
    </Layout>
    </div>
  );
}
